import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import LoadCurrencies from '../../request/Countries/LoadCurrencies';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Autocomplete,
  Link,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  TrendingUp,
  TrendingDown,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PRateManagement = ({ partnerId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fundingBal = useSelector((state) => state.wallet.fundingBalance);
  
  // Get rates from unified user data
  const userData = useSelector((state) => state.userData?.user);
  const rates = userData?.p2p_partner?.rates || [];
  
  // Get coinRates from Redux store (like CoinMarket component)
  const coinRates = useSelector((state) => state.coins.coinRates);
  
  // Load currencies from API
  const { data: currenciesData } = LoadCurrencies();
  const currencies = currenciesData?.currencies || [];
  
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    cryptocurrency: '',
    fiat_currency: '',
    trade_type: '',
    rate: '',
    min_amount: '',
    max_amount: '',
    available_balance: '',
    payment_methods: [],
    payment_details: {}, // New field for payment method details
    terms_conditions: '',
    time_limit: 30,
    margin_percentage: 0,
    is_active: true
  });

  // Get cryptocurrencies from coinRates (like CoinMarket component)
  const cryptocurrencies = coinRates.map(coin => coin.symbol).filter(symbol => 
    ['BTC', 'ETH', 'USDT', 'USDC', 'BNB', 'ADA', 'DOT', 'SOL', 'XRP', 'LTC', 'BCH', 'DOGE', 'DASH', 'XMR', 'XLM', 'TRX'].includes(symbol)
  );
  
  // Get unique fiat currencies from API data
  const fiatCurrencies = [...new Set(currencies.map(currency => currency.currency_code))];
  
  const paymentMethods = [
    'Bank Transfer', 'Mobile Money', 'PayPal', 'Skrill', 'Neteller',
    'Perfect Money', 'Payoneer', 'Cash Deposit'
  ];

  // Rates are now loaded from unified user data
  useEffect(() => {
    setLoading(false);
  }, [rates]);

  const handleOpenDialog = (rate = null) => {
    if (rate) {
      setEditingRate(rate);
      setFormData({
        ...rate,
        payment_methods: rate.payment_methods || [],
        payment_details: rate.payment_details || {}
      });
    } else {
      setEditingRate(null);
      setFormData({
        cryptocurrency: '',
        fiat_currency: '',
        trade_type: '',
        rate: '',
        min_amount: '',
        max_amount: '',
        available_balance: fundingBal || '0',
        payment_methods: [],
        payment_details: {},
        terms_conditions: '',
        time_limit: 30,
        margin_percentage: 0,
        is_active: true
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRate(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePaymentMethodChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      payment_methods: value,
      // Reset payment details when payment methods change
      payment_details: value.reduce((acc, method) => {
        acc[method] = prev.payment_details[method] || {};
        return acc;
      }, {})
    }));
  };

  const handlePaymentDetailChange = (method, field, value) => {
    setFormData(prev => ({
      ...prev,
      payment_details: {
        ...prev.payment_details,
        [method]: {
          ...prev.payment_details[method],
          [field]: value
        }
      }
    }));
  };

  const getPaymentDetailFields = (method) => {
    switch (method) {
      case 'Bank Transfer':
        return [
          { key: 'bank_name', label: 'Bank Name', required: true },
          { key: 'account_number', label: 'Account Number', required: true },
          { key: 'account_name', label: 'Account Name', required: true },
          { key: 'routing_number', label: 'Routing Number (if applicable)', required: false },
          { key: 'swift_code', label: 'SWIFT Code (for international)', required: false }
        ];
      case 'Mobile Money':
        return [
          { key: 'provider', label: 'Mobile Money Provider', required: true },
          { key: 'phone_number', label: 'Phone Number', required: true },
          { key: 'account_name', label: 'Account Name', required: true }
        ];
      case 'PayPal':
        return [
          { key: 'email', label: 'PayPal Email', required: true },
          { key: 'account_name', label: 'Account Name', required: true }
        ];
      case 'Skrill':
        return [
          { key: 'email', label: 'Skrill Email', required: true },
          { key: 'account_name', label: 'Account Name', required: true }
        ];
      case 'Neteller':
        return [
          { key: 'email', label: 'Neteller Email', required: true },
          { key: 'account_id', label: 'Account ID', required: true },
          { key: 'account_name', label: 'Account Name', required: true }
        ];
      case 'Perfect Money':
        return [
          { key: 'account_number', label: 'Perfect Money Account', required: true },
          { key: 'account_name', label: 'Account Name', required: true }
        ];
      case 'Payoneer':
        return [
          { key: 'email', label: 'Payoneer Email', required: true },
          { key: 'account_name', label: 'Account Name', required: true }
        ];
      case 'Cash Deposit':
        return [
          { key: 'location', label: 'Deposit Location/Instructions', required: true },
          { key: 'contact_info', label: 'Contact Information', required: true }
        ];
      default:
        return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate balance
    const balance = parseFloat(formData.available_balance);
    const minAmount = parseFloat(formData.min_amount);
    
    if (balance === 0) {
      setError('You need to deposit funds before creating a P2P rate. Please visit the wallet page to make a deposit.');
      setLoading(false);
      return;
    }
    
    if (balance < minAmount) {
      setError(`Your available balance ($${balance}) is less than the minimum amount ($${minAmount}). Please deposit more funds or reduce the minimum amount.`);
      setLoading(false);
      return;
    }

    try {
      const { default: p2pPartnerService } = await import('../../services/p2pPartner.service');
      
      let data;
      if (editingRate) {
        data = await p2pPartnerService.updateRate(editingRate.id, formData);
      } else {
        data = await p2pPartnerService.createRate(formData);
      }

      if (data.success) {
        setSuccess(editingRate ? 'Rate updated successfully!' : 'Rate created successfully!');
        // Refresh the page to reload data from unified system
        window.location.reload();
        setTimeout(() => {
          handleCloseDialog();
        }, 1500);
      } else {
        setError(data.message || 'Failed to save rate.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Network error. Please try again.';
      
      // Handle specific error for unapproved partners
      if (errorMessage.includes('Partner profile not approved') || errorMessage.includes('not approved or active')) {
        setError('Your P2P partner application is still under review. You can only create rates after admin approval. Please check your application status.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this rate?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'https://api.imexsbrokerage.com';
      const response = await fetch(`${baseURL}/api/p2p-partner/rates/${rateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh the page to reload data from unified system
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting rate:', error);
    }
  };

  const toggleRateStatus = async (rate) => {
    try {
      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'https://api.imexsbrokerage.com';
      const response = await fetch(`${baseURL}/api/p2p-partner/rates/${rate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !rate.is_active }),
      });

      if (response.ok) {
        // Refresh the page to reload data from unified system
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating rate status:', error);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Rate Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Rate
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pair</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rate</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Limits</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Balance</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map((rate, index) => (
              <motion.tr
                key={rate.id}
                component={TableRow}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                sx={{
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {rate.cryptocurrency}/{rate.fiat_currency}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={rate.trade_type === 'buy' ? <TrendingUp /> : <TrendingDown />}
                    label={rate.trade_type.toUpperCase()}
                    color={rate.trade_type === 'buy' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(rate.rate, rate.fiat_currency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatCurrency(rate.min_amount, rate.fiat_currency)} - {formatCurrency(rate.max_amount, rate.fiat_currency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {rate.available_balance} {rate.cryptocurrency}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={rate.is_active ? 'Active' : 'Inactive'}
                      color={rate.is_active ? 'success' : 'default'}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => toggleRateStatus(rate)}
                      color={rate.is_active ? 'default' : 'primary'}
                    >
                      {rate.is_active ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(rate)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(rate.id)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {rates.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="text.secondary">
            No rates found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first rate to start accepting P2P orders
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Your First Rate
          </Button>
        </Paper>
      )}

      {/* Rate Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRate ? 'Edit Rate' : 'Create New Rate'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={cryptocurrencies}
                  value={formData.cryptocurrency}
                  onChange={(event, value) => handleInputChange('cryptocurrency', value || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Cryptocurrency" required />
                  )}
                  renderOption={(props, option) => {
                    const coin = coinRates.find(c => c.symbol === option);
                    return (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img 
                          src={`https://assets.coincap.io/assets/icons/${option.toLowerCase()}@2x.png`}
                          alt={option}
                          style={{ width: 24, height: 24, borderRadius: '50%' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {option}
                        </Typography>
                        {coin && (
                          <Typography variant="body2" color="text.secondary">
                            - {coin.name}
                          </Typography>
                        )}
                      </Box>
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={currencies}
                  getOptionLabel={(option) => option.currency_code || ''}
                  value={currencies.find(c => c.currency_code === formData.fiat_currency) || null}
                  onChange={(event, value) => handleInputChange('fiat_currency', value?.currency_code || '')}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option =>
                      option.currency_code.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option.currency.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option.country_name.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.currency_code === value.currency_code}
                  renderInput={(params) => (
                    <TextField {...params} label="Fiat Currency" required placeholder="Search currencies..." />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img 
                        src={option.flag} 
                        alt={option.country_name}
                        style={{ width: 24, height: 18, borderRadius: '2px', objectFit: 'cover' }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {option.currency_code}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({option.currency})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        {option.country_name}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={['buy', 'sell']}
                  value={formData.trade_type}
                  onChange={(event, value) => handleInputChange('trade_type', value || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Trade Type" required />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rate"
                  type="number"
                  value={formData.rate}
                  onChange={(e) => handleInputChange('rate', e.target.value)}
                  required
                  inputProps={{ step: '0.00000001' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Amount"
                  type="number"
                  value={formData.min_amount}
                  onChange={(e) => handleInputChange('min_amount', e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.fiat_currency || 'USD'}
                      </InputAdornment>
                    ),
                  }}
                  helperText="Minimum fiat amount for this rate"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Amount"
                  type="number"
                  value={formData.max_amount}
                  onChange={(e) => handleInputChange('max_amount', e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.fiat_currency || 'USD'}
                      </InputAdornment>
                    ),
                  }}
                  helperText="Maximum fiat amount for this rate"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Available Balance"
                  type="number"
                  value={formData.available_balance}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        USD
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        This is your current total assets balance
                      </Typography>
                      {parseFloat(formData.available_balance) === 0 && (
                        <Link component={RouterLink} to="/wallet" variant="caption" color="primary">
                          Deposit funds to start trading
                        </Link>
                      )}
                    </Box>
                  }
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      color: parseFloat(formData.available_balance) === 0 ? 'error.main' : 'text.primary',
                      fontWeight: 'bold'
                    } 
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Time Limit (minutes)"
                  type="number"
                  value={formData.time_limit}
                  onChange={(e) => handleInputChange('time_limit', parseInt(e.target.value))}
                  required
                  inputProps={{ min: 5, max: 120 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={paymentMethods}
                  value={formData.payment_methods}
                  onChange={handlePaymentMethodChange}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        color="primary"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Methods"
                      placeholder="Select payment methods"
                      required
                    />
                  )}
                />
              </Grid>

              {/* Payment Method Details */}
              {formData.payment_methods.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Payment Method Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Provide your account details for each payment method. This information will be shown to buyers for payment.
                  </Typography>
                  
                  {formData.payment_methods.map((method, methodIndex) => (
                    <Card key={method} sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                          {method} Details
                        </Typography>
                        <Grid container spacing={2}>
                          {getPaymentDetailFields(method).map((field, fieldIndex) => (
                            <Grid item xs={12} md={6} key={field.key}>
                              <TextField
                                fullWidth
                                label={field.label}
                                value={formData.payment_details[method]?.[field.key] || ''}
                                onChange={(e) => handlePaymentDetailChange(method, field.key, e.target.value)}
                                required={field.required}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                variant="outlined"
                                size="small"
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Terms & Conditions"
                  multiline
                  rows={3}
                  value={formData.terms_conditions}
                  onChange={(e) => handleInputChange('terms_conditions', e.target.value)}
                  placeholder="Enter any specific terms or conditions for this rate..."
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || (parseFloat(formData.available_balance) === 0 && !editingRate)}
          >
            {loading ? 'Saving...' : (editingRate ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default P2PRateManagement;
